import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormGroupDirective, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { MultiSelect, MultiSelectChangeEvent } from 'primeng/multiselect';
import { Table } from 'primeng/table';
import { BehaviorSubject, forkJoin, Observable, Subject, take, takeUntil } from 'rxjs';
import { LocationService } from 'src/app/services/location.service';
import { PeopleService } from 'src/app/services/people.service';
import { AppConstants } from 'src/app/shared/app.constants';
import { AppPermissions } from 'src/app/shared/app.permissions';
import { EventStepperDataService } from './event-stepper-data.service';

@Component({
  selector: 'ness-event-people',
  templateUrl: './event-people.component.html'
})
export class EventPeopleComponent implements OnInit {
  @ViewChild('peopleTable', { static: false }) peopleTable: Table;
  @ViewChild('dt', { static: false }) findPeopleTable: Table;
  @ViewChild('pt', { static: false }) personTypesMultiSelect: MultiSelect;

  private _filterFormValidator: ValidatorFn = (formGroup: FormGroup): ValidationErrors | null => {
    let isValid: boolean = true;

    if (formGroup && formGroup.getRawValue()) {
      switch (this.filterCtrlType) {
        case 'FilterInput':
          isValid = !(formGroup.get('FilterInput').value == null || formGroup.get('FilterInput').value.length === 0);
          break;
        case 'SSN':
          isValid = formGroup.get('FilterSSN').value.replace(/[_|-]/g, '').length > 0;
          break;
        case 'Name':
          isValid = !!formGroup.get('FilterFirstNameInput').value.replace(' ', '').concat(formGroup.get('FilterLastNameInput').value.replace(' ', '')).length;
          break;
        case 'DateOfBirth':
          isValid = !(formGroup.get('FilterDate').value == null || formGroup.get('FilterDate').value.length === 0);
          break;
        case 'PhoneNumber':
          isValid = formGroup.get('FilterPhoneNumber').value.replace(/[_|-]/g, '').length > 0;
          break;
      }

    }
    return isValid ? null : { 'filterFormInvalid': true };
  };

  addEditLocationTitleText: string = null;
  addEditPersonTitleText: string = null;
  displaySidebar: boolean = false;
  eventEditPerson$: Observable<ServerModels.ENESS_PersonDTO>;
  eventForm: FormGroup = <FormGroup>this.controlContainer.form;
  eventPeopleFormControl: FormControl = this.eventForm.get('ENESS_XREF_EventsPeople') as FormControl;
  filterCtrlType: string = 'FilterInput';
  findPersonFormGroup: FormGroup = this.fb.group({
    FilterID: ['', Validators.required],
    FilterInput: '',
    FilterDate: '',
    FilterSSN: '',
    FilterFirstNameInput: '',
    FilterLastNameInput: '',
    FilterPhoneNumber: ''
  }, { validator: this._filterFormValidator });
  isPersonStaging: boolean = false;
  isEventAddPersonSearch: boolean = false;
  maxDateRule: Date = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
  openAddEditPersonForm: boolean = false;
  openViewPersonDetailForm: boolean = false;
  peopleSearchResultTableCols: any[] = [
    { field: 'ENESS_XREF_PeopleFBIFileNumbers', subfield: 'FBIFileNumber', header: 'FBI #' },
    { field: 'ENESS_XREF_PeopleStateIDNumbers', subfield: 'StateIDNumbers', header: 'State ID' },
    { field: 'LastName', header: 'Last Name' },
    { field: 'FirstName', header: 'First Name' },
    { field: 'DateOfBirth', header: 'Birth Date' },
    { field: 'SSN', header: 'SSN' }
  ];
  personFormMode: string = null;
  rows: number = AppConstants.defaultTableRows;
  selectedPeople: ServerModels.FindPeople[] = [];
  openNewLocationForm: boolean = false;
  openFindExistingLocationForm: boolean = false;
  fromPeopleLocationForm: boolean = false;
  associationButtonLabel: string = "Associate with person";
  existingLocationAssociationButtonLabel: string = "Associate selected address to person";
  openViewPersonAddressDetails: boolean = false;
  openViewPersonAddressDetailForm: boolean = false;
  hasPermissionToEditAddress: boolean = false;
  personAddressIsStaging: boolean = false;
  personAddressType: string;
  personAddressTypeFromRef: string;
  personAddressTypeOther: string;
  searchAddress$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  showLocationView: boolean = false;
  showEditLocationView: boolean = false;
  personAddress: ServerModels.ENESS_AddressDTO;
  viewPersonID: string = null;
  findPersonPerformed: boolean = false;
  protected destroyed$: Subject<void> = new Subject();

  public get people(): ServerModels.ENESS_XREF_EventsPeopleDTO[] {
    return this.eventPeopleFormControl.value || [];
  }

  constructor(public controlContainer: FormGroupDirective,
    public eventDataSvc: EventStepperDataService,
    public peopleService: PeopleService,
    public locationService: LocationService,
    private messageService: MessageService,
    private hostElement: ElementRef,
    private fb: FormBuilder) { }

  ngOnInit() {
    this.findPersonFormGroup.get('FilterID').valueChanges.subscribe(
      (filterId:string) => {
        this._onPersonFilterValueChange(filterId);
      }
    );
    this.eventDataSvc.setPeopleResults([]);
  }

  ngOnDestroy() {
    this.messageService.clear('eventPersonMsg');
    this.personFormMode = null;
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngAfterViewInit() {
    window.setTimeout(() => {
      if (typeof this.personTypesMultiSelect !== 'undefined') {
        (<HTMLElement>this.personTypesMultiSelect.el.nativeElement).querySelector('.p-hidden-accessible > input').setAttribute('aria-required', 'true');
      }
    }, 2000);
  }

  public associateSelectedPeople(): void {
    this.messageService.clear();
    let people = this.selectedPeople.map((person: ServerModels.FindPeople) => {
      if (!this._isDuplicate(person.PersonID)) {
        return <ServerModels.ENESS_XREF_EventsPeopleDTO>{
          EventPersonStagingID: null,
          EventStagingID: this.eventForm.get('EventStagingID').value,
          EventPersonID: null,
          EventID: this.eventForm.get('EventID').value,
          PersonID: person.PersonID,
          PersonStagingID: null,
          CreatedOn: null,
          ENESS_XREF_EventsPeopleTypes: []
        };
      } else {
        return null;
      }
    });

    people = people.filter(x => !!x);

    this.eventDataSvc.copyEventPeopleToStaging(people).pipe(takeUntil(this.destroyed$)).subscribe((newPeople: ServerModels.ENESS_XREF_EventsPeopleDTO[]) => {
      this.eventPeopleFormControl.setValue([...newPeople, ...this.people]);
      this._resetFindPersonForm();
      this.eventDataSvc.stepStatusChange({ step: 'people', state: false });
    },
      (err:any) => {
        if (err.status !== 0) {
          this.messageService.add({ key: 'eventPersonMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage || err.error.Message });
        }
      }
    );
  }

  public getFBIFileNumbers(data: ServerModels.ENESS_XREF_PeopleFBIFileNumbersDTO[]): string[] {
    return data.map(fbi => fbi.FBIFileNumber);
  }

  public getStateIDNumbers(data: ServerModels.ENESS_XREF_PeopleStateIDNumbersDTO[]): string[] {
    const result = data.map(sid => {
      const abbrevation = sid.StateAbbreviation ?? "";
      const abbrevationWithColon = abbrevation ? abbrevation.concat(': ') : "";
      return `${abbrevationWithColon}${sid.StateIDNumbers}`;
    });
    return result;
  }

  public onEditPerson(personStagingId: string) {
    if (!personStagingId) {
      this.messageService.add({ key: 'personDetailMsg', severity: 'error', summary: 'Error:', detail: 'Person ID missing. Unable to retrieve person data.' });
    }
    this.displaySidebar = true;
    this.openViewPersonDetailForm = false;
    this.personFormMode = 'edit';
    this.isPersonStaging = this._isDuplicate(personStagingId);
    this.openAddEditPersonForm = true;
    this.addEditPersonTitleText = 'Edit person';
    if (this.eventDataSvc.getPersonDetailsValue().PersonStagingID !== personStagingId) {
      this.eventDataSvc.getPersonDetails(personStagingId, 'personFormMsg');
    } else {
      this.eventDataSvc.setPersonDetails(this.eventDataSvc.getPersonDetailsValue());
    }
  }

  public onPersonSaved(personDTO: ServerModels.ENESS_PersonDTO): void {
    if (!personDTO || !Object.keys(personDTO).length)
      return;

    this._resetFindPersonForm();
    if (this.personFormMode == 'edit') {
      this._saveEventPerson(personDTO);
    } else {
      this._addNewEventPerson(personDTO);
    }
    this.findPersonPerformed = false;
    this.eventDataSvc.setPersonDetails(null);
  }

  private _personTypeSave(eventPersonIndex: number): void {
    if (isNaN(eventPersonIndex) || eventPersonIndex < 0)
      return;

    let changedPerson: ServerModels.ENESS_XREF_EventsPeopleDTO = this.people[eventPersonIndex];
    if (!!changedPerson) {
      this.eventDataSvc.addOrUpdateEventPeopleTypesStaging(changedPerson).pipe(takeUntil(this.destroyed$)).subscribe((person: ServerModels.ENESS_XREF_EventsPeopleDTO) => {
        this.people[eventPersonIndex] = person;
        this.eventPeopleFormControl.setValue([...this.people]);
        this.eventDataSvc.stepStatusChange({ step: 'people', state: !this._isMissingPersonType() });
      },
        (err:any) => {
          if (err.status !== 0) {
            this.messageService.add({ key: 'eventPersonMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage || err.error.Message });
          }
        }
      );
    }
  }

  public onPersonTypeChange(evt: MultiSelectChangeEvent, eventPersonIndex: number): void {
    if (evt.value.length == 0) {
      if (!evt.itemValue.hasOwnProperty('EventPersonTypeStagingID')) {
        this.people[eventPersonIndex].ENESS_XREF_EventsPeopleTypes = [];
        this._personTypeSave(eventPersonIndex);
      }
    } else if (evt.value.findIndex(x => x.PersonTypeID == evt.itemValue.PersonTypeID) == -1) {
      this._personTypeSave(eventPersonIndex);
    } else {
      for (let i = 0; i < evt.value.length; i++) {
        if (evt.value[i]['PersonTypeID'] == evt.itemValue['PersonTypeID']) {
          if (!evt.itemValue.hasOwnProperty('EventPersonTypeStagingID')) {
            let eventPersonType = <ServerModels.ENESS_XREF_EventsPeopleTypesDTO>{
              EventPersonTypeStagingID: null,
              EventPersonTypeID: null,
              EventPersonStagingID: this.people[eventPersonIndex].EventPersonStagingID,
              EventPersonID: this.people[eventPersonIndex].EventPersonID,
              PersonTypeID: evt.itemValue['PersonTypeID'],
              PersonTypeName: evt.itemValue['PersonTypeName'],
              PersonTypeAbbreviation: evt.itemValue['PersonTypeAbbreviation'],
              CreatedOn: null
            };
            this._setEventPersonTypes(eventPersonType, eventPersonIndex);
            this._personTypeSave(eventPersonIndex);
          }
        }
      }
    }
  }

  public onSearchPerson() {
    const searchFilterId: string = this.findPersonFormGroup.get('FilterID').value;
    let peopleFilterValues: ServerModels.FilterValues[] = [];
    let filterValue: ServerModels.FilterValues = {
      FilterColumnName: searchFilterId,
      FilterValue: null,
      DateFilterValue: null
    } as ServerModels.FilterValues;

    if (searchFilterId === 'Name') {
      filterValue.FilterValue = (!!this.findPersonFormGroup.get('FilterFirstNameInput').value && !!this.findPersonFormGroup.get('FilterLastNameInput').value) ? `${this.findPersonFormGroup.get('FilterFirstNameInput').value} ${this.findPersonFormGroup.get('FilterLastNameInput').value}`.trim() : (!!this.findPersonFormGroup.get('FilterFirstNameInput').value) ? `FirstName:${this.findPersonFormGroup.get('FilterFirstNameInput').value.trim()}` : `LastName:${this.findPersonFormGroup.get('FilterLastNameInput').value.trim()}`;
    } else if (searchFilterId === 'DateOfBirth') {
      filterValue.FilterValue = this.findPersonFormGroup.get('FilterDate').value;
    } else if (searchFilterId === 'SSN') {
      filterValue.FilterValue = this.findPersonFormGroup.get('FilterSSN').value.trim().replace(/_/g, '');
    } else if (searchFilterId === 'PhoneNumber') {
      let phone: string = this.findPersonFormGroup.get('FilterPhoneNumber').value;
      if (!!phone) {
        phone = phone.trim().replace(/\D/g, ''); // keep only digits
        if (phone.length > 3) {
          filterValue.FilterValue = phone;
        }
      }
    } else {
      filterValue.FilterValue = this.findPersonFormGroup.get('FilterInput').value.trim();
    }
    this.findPersonPerformed = true;
    peopleFilterValues.push(filterValue);
    this.selectedPeople = [];
    this.eventDataSvc.setPeopleResults([]);
    this.findPeopleTable.reset();
    this.getPersonRearchResult(peopleFilterValues, this.people);
  }

  private getPersonRearchResult(filterValues: ServerModels.FilterValues[], excludePersons: ServerModels.ENESS_XREF_EventsPeopleDTO[]) {
    this.eventDataSvc._peopleResults = [];
    this.eventDataSvc.peopleResults.next(this.eventDataSvc._peopleResults);

    if (!!filterValues && filterValues.length > 0 && !!filterValues[0].FilterValue) {
      this.peopleService.findPeople(filterValues).pipe(take(1)).subscribe((people: ServerModels.ENESS_PersonDTO[]) => {
        this.eventDataSvc._peopleResults = [...<ServerModels.FindPeople[]>people].filter(p => !excludePersons.some(e => p.PersonID == e.PersonID));
        this.eventDataSvc.peopleResults.next(this.eventDataSvc._peopleResults);

        if (people.length > 0) {
          this.resetPersonFormFields();
        }
      },
        (err:any) => this.messageService.add({ key: 'eventPersonMsg', severity: 'error', summary: 'Error:', detail: err.error })
      );
    }
  }

  public onSelectFilterType(value: any) {
    this.findPersonPerformed = false;
  }

  public onHideSidebar(): void {
    this.eventDataSvc.setPersonDetails(null);
    this.openAddEditPersonForm = false;
    this.openViewPersonDetailForm = false;
    this.openViewPersonAddressDetails = false;
    this.openViewPersonAddressDetailForm = false;
    this.openNewLocationForm = false;
    this.openFindExistingLocationForm = false;
    this.isEventAddPersonSearch = false;
    this.personAddress = null;
  }

  public onShowSidebar(): void {
    let targetEl = this.hostElement.nativeElement.getElementsByClassName('ui-sidebar-active');
    if (targetEl.length) {
      targetEl[0].scrollTop = 0;
      targetEl[0].setAttribute("tabindex", "-1");
      targetEl[0].focus();
    }
  }

  public onAddPersonClick(): void {
    this.messageService.clear();
    this.displaySidebar = true;
    this.openAddEditPersonForm = true;
    this.personFormMode = 'add';
    this.addEditPersonTitleText = 'Add a new person';
    this.isPersonStaging = true;
    let emptyPerson = this.peopleService.createEmptyPersonDTO();
    emptyPerson = this.populatePersonFormBasedOnSearchTerms(emptyPerson);
    this.peopleService.addOrUpdatePersonStagingAsync(emptyPerson).pipe(takeUntil(this.destroyed$)).subscribe((newPerson: ServerModels.ENESS_PersonDTO) => {
      newPerson.GenderID = '';
      newPerson.RaceID = '';
      this.eventDataSvc.setPersonDetails(newPerson);
    },
      (err:any) => {
        if (err.status !== 0) {
          this.messageService.add({ key: 'eventPersonMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage || err.error.Message });
        }
      }
    );
  }

  private populatePersonFormBasedOnSearchTerms(person: ServerModels.ENESS_PersonDTO): ServerModels.ENESS_PersonDTO {
    const searchFilterId: string = this.findPersonFormGroup.get('FilterID').value;
    if (this.findPersonPerformed) {

      if (searchFilterId === 'Name') {
        person.FirstName = this.findPersonFormGroup.get('FilterFirstNameInput').value?.trim();
        person.LastName = this.findPersonFormGroup.get('FilterLastNameInput').value?.trim();
      }
    }

    return person;
  }

  public onEditPersonClose(e:string): void {
    this.isEventAddPersonSearch = false;
    if (e == "FindExistingAddress") {
      this.openAddEditPersonForm = false;
      this.openFindExistingLocationForm = true;
      this.displaySidebar = true;
    }
    else if (e == "FindNewAddress") {
      this.openAddEditPersonForm = false;
      this.openNewLocationForm = true;
      this.displaySidebar = true;
    }
    else {
      this.personFormMode = null;
      this.isPersonStaging = false;
      this.addEditPersonTitleText = null;
      this.eventDataSvc.setPersonDetails(null);
      this.openAddEditPersonForm = false;
      this.displaySidebar = false;
    }
  }

  public onViewPersonDetailsClose(): void {
    this.eventDataSvc.setPersonDetails(null);
    this.peopleService.personLECasesSubj.next(null);
    this.peopleService.personInfoReportsSubj.next(null);
    this.peopleService.personResultsSubj.next(null);
    this.isEventAddPersonSearch = false;
    this.openViewPersonDetailForm = false;
    this.displaySidebar = false;
  }

  public removePerson(idx: number): void {
    let eventPersonStagingId: string = this.people[idx].EventPersonStagingID;
    if (!eventPersonStagingId)
      return;

    this.eventDataSvc.removeEventPerson(eventPersonStagingId).subscribe((result: number) => {
      this._onRemovePerson(idx);
      this.eventDataSvc.stepStatusChange({ step: 'people', state: !this._isMissingPersonType() });
    },
      (err:any) => {
        if (err.status !== 0) {
          this.messageService.add({ key: 'eventPersonMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage });
        }
      }
    );
  }

  public viewPersonDetails(personStagingId: string, personId: string, source: string): void {
    this.displaySidebar = true;
    this.openViewPersonDetailForm = true;
    this.viewPersonID = null;

    if (source == 'associated') {
      this._viewAssociatedPersonDetails(personStagingId);
    }
    if (source == 'find') {
      this.viewPersonID = personId;
      this._viewFindPersonDetails(personId);
    }
    if (!!personId) {
      this._getPersonLECases(personId);
      this._getPersonInfoReports(personId);
      this._getPersonResults(personId);
    } else {
      this.peopleService.personLECasesSubj.next([]);
      this.peopleService.personInfoReportsSubj.next([]);
      this.peopleService.personResultsSubj.next([]);
    }
  }

  private _viewFindPersonDetails(personId: string): void {
    this.isEventAddPersonSearch = true;
    this.eventDataSvc.getPersonDetails(personId, 'personDetailMsg');
  }

  private _viewAssociatedPersonDetails(personStagingId: string): void {
    this.isEventAddPersonSearch = false;
    this.eventDataSvc.getPersonDetailsStaging(personStagingId, 'personDetailMsg');
  }

  public viewAddressDetails(addressDetails: any[]): void {
    this.displaySidebar = true;
    this.openViewPersonDetailForm = false;
    this.openViewPersonAddressDetails = true;
    this.openViewPersonAddressDetailForm = false;
    this.hasPermissionToEditAddress = addressDetails[1];
    this.personAddressTypeFromRef = addressDetails[2];
    this.personAddressTypeOther = addressDetails[3];
    this.personAddressType = addressDetails[2] === "Other" ? addressDetails[2] + ": " + addressDetails[3] : addressDetails[2];
    if (addressDetails[4]) {
      this.personAddressIsStaging = true;
      forkJoin({
        address: this.locationService.getAddressDetailsStagingById(addressDetails[0]),
        leCases: this.locationService.getAddressLECases(addressDetails[0]),
        people: this.locationService.getAddressPeople(addressDetails[0])
      }).pipe(take(1)).subscribe({
        next: (val: { address: ServerModels.ENESS_AddressDTO, leCases: ServerModels.AddressLECases[], people: ServerModels.AddressPeople[]  }) => {
          this.showLocationView = true;
          this.personAddress = val.address;
          this.locationService.addressLECasesSubj.next(val.leCases);
          this.locationService.addressPeopleSubj.next(val.people);
        },
        error: (err:any) => {
          this.messageService.addAll([{ key: 'viewAddressDetailsMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage }]);
        }
      })
    }
    else {
      this.personAddressIsStaging = false;
      forkJoin({
        address: this.locationService.getAddressDetailsById(addressDetails[0]),
        leCases: this.locationService.getAddressLECases(addressDetails[0]),
        people: this.locationService.getAddressPeople(addressDetails[0])
      }).pipe(take(1)).subscribe({
        next: (val: { address: ServerModels.ENESS_AddressDTO, leCases: ServerModels.AddressLECases[], people: ServerModels.AddressPeople[] }) => {
          this.showLocationView = true;
          this.personAddress = val.address;
          this.locationService.addressLECasesSubj.next(val.leCases);
          this.locationService.addressPeopleSubj.next(val.people);
        },
        error: (err:any) => {
          this.messageService.addAll([{ key: 'viewAddressDetailsMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage }]);
        }
      })
    }
  }

  public editLocation(editLocation: string): void {
    if (editLocation === 'Edit') {
      this.openViewPersonDetailForm = false;
      this.openViewPersonAddressDetails = false;
      this.openViewPersonAddressDetailForm = true;
      this.showLocationView = false;
      this.showEditLocationView = true;
    }
  }

  public onAddExistingLocations(newAddresses: ServerModels.ENESS_AddressDTO[]): void {
    this.messageService.clear();

    if (newAddresses != null && !!Object.keys(newAddresses).length) {
      let _personDetails = this.eventDataSvc.getPersonDetailsValue();

      newAddresses.forEach((loc, idx) => {
        if (_personDetails.ENESS_XREF_PeopleAddresses.some(x => x.AddressID === loc.AddressID)) {
          newAddresses.splice(idx, 1);
          this.messageService.add({ key: 'personAddressMsg', severity: 'info', summary: 'Info:', detail: `"${loc.StreetAddress}" is already associated with this person.` });
        } else {
          loc.AddressStagingID = null;
          let _address = {
            PersonID: _personDetails.PersonID,
            AddressTypeID: null,
            OtherAddressType: null,
            ENESS_Address: loc,
            AddressID: loc.AddressID,
            AddressStagingID: null,
            IsCurrent: false
          } as ServerModels.ENESS_XREF_PeopleAddressesDTO;
          _personDetails.ENESS_XREF_PeopleAddresses.push(_address);
        }
      }, this);

      this.eventDataSvc.setPersonDetails(_personDetails);

      this.fromPeopleLocationForm = true;
      this.openAddEditPersonForm = true;
      this.openFindExistingLocationForm = false;
    }
  }

  public onAddExistingLocationsClose() {
    this.messageService.clear();
    this.openAddEditPersonForm = true;
    this.openFindExistingLocationForm = false;
    this.fromPeopleLocationForm = true;
  }

  public onNewLocationAdded(newAddress: ServerModels.ENESS_AddressDTO): void {
    this.messageService.clear();

    if (newAddress != null && !!Object.keys(newAddress).length) {
      newAddress.AddressStagingID = null;
      let _personDetails = this.eventDataSvc.getPersonDetailsValue();
      let _address = {
        PeopleAddressesStagingID: null,
        PeopleAddressesID: null,
        PersonStagingID: _personDetails.PersonStagingID,
        PersonID: _personDetails.PersonID,
        AddressTypeID: null,
        OtherAddressType: null,
        ENESS_Address: newAddress,
        AddressID: newAddress.AddressID,
        AddressStagingID: newAddress.AddressStagingID,
        IsCurrent: false,
        CreatedOn: null,
        CreatedBy: null
      } as ServerModels.ENESS_XREF_PeopleAddressesDTO;
      _personDetails.ENESS_XREF_PeopleAddresses.push(_address);

      this.fromPeopleLocationForm = true;
      this.openAddEditPersonForm = true;
      this.openNewLocationForm = false;
    }
  }

  public onNewLocationClose(): void {
    this.openNewLocationForm = false;
    this.displaySidebar = true;
    this.openAddEditPersonForm = true;
  }

  public onLocationFormClose(newAddress: ServerModels.ENESS_AddressDTO): void {
    if (this.personAddressIsStaging) {
      this.viewAddressDetails([newAddress.AddressStagingID, true, this.personAddressTypeFromRef, this.personAddressTypeOther, true]);
    }
    else {
      this.viewAddressDetails([newAddress.AddressID, true, this.personAddressTypeFromRef, this.personAddressTypeOther, false]);
    }
  }

  public onClickCloseButton(): void {
    if (this.openViewPersonAddressDetails) {
      this.openViewPersonAddressDetails = false;
      if (this.viewPersonID) {
        this._viewFindPersonDetails(this.viewPersonID);
      }
      this.openViewPersonDetailForm = true;
    }
    if (this.openViewPersonAddressDetailForm) {
      this.viewAddressDetails([this.personAddress.AddressID, true, this.personAddressTypeFromRef, this.personAddressTypeOther]);
    }
  }

  private _addNewEventPerson(newPerson: ServerModels.ENESS_PersonDTO): void {
    if (!newPerson || !Object.keys(newPerson).length)
      return;

    let eventPersonDTO = <ServerModels.ENESS_XREF_EventsPeopleDTO>{
      EventPersonStagingID: null,
      EventStagingID: this.eventForm.get('EventStagingID').value,
      EventPersonID: null,
      EventID: this.eventForm.get('EventID').value,
      PersonID: null,
      PersonStagingID: newPerson.PersonStagingID,
      CreatedOn: null,
      ENESS_XREF_EventsPeopleTypes: []
    };

    this.eventDataSvc.addOrUpdateEventPersonStaging(eventPersonDTO).pipe(takeUntil(this.destroyed$)).subscribe((personResult: ServerModels.ENESS_XREF_EventsPeopleDTO) => {
      this.eventPeopleFormControl.setValue([personResult, ...this.people]);
      this.eventDataSvc.stepStatusChange({ step: 'people', state: false });
      this.openAddEditPersonForm = false;
      this.displaySidebar = false;
    },
      (err:any) => this.peopleService.messageService.add({ key: 'personFormMsg', severity: 'error', summary: 'Error', detail: err.error || err.error.ExceptionMessage || err.error.Message })
    );
  }

  private _saveEventPerson(updatedPerson: ServerModels.ENESS_PersonDTO): void {
    if (this.isPersonStaging) {
      this.eventDataSvc.setPersonDetails(updatedPerson);
      // Update Event People Display Value
      let index = this.people.findIndex(x => x.PersonStagingID == updatedPerson.PersonStagingID)
      if (index > -1) {
        this.people[index].ENESS_XREF_PeopleFBIFileNumbers = updatedPerson.ENESS_XREF_PeopleFBIFileNumbers;
        this.people[index].ENESS_XREF_PeopleStateIDNumbers = updatedPerson.ENESS_XREF_PeopleStateIDNumbers;
        this.people[index].FirstName = updatedPerson.FirstName;
        this.people[index].LastName = updatedPerson.LastName;
        this.people[index].SSN = updatedPerson.SSN;
        this.people[index].DateOfBirth = updatedPerson.DateOfBirth;
      }
    } else {
      if (!this.isPersonStaging && this.eventDataSvc.getPeopleResultsValue().length > 0) {
        this.eventDataSvc.getPeopleResultsValue().forEach(p => {
          if (p.PersonID == updatedPerson.PersonID) {
            p.ENESS_XREF_PeopleFBIFileNumbers = updatedPerson.ENESS_XREF_PeopleFBIFileNumbers;
            p.ENESS_XREF_PeopleStateIDNumbers = updatedPerson.ENESS_XREF_PeopleStateIDNumbers;
            p.FirstName = updatedPerson.FirstName;
            p.LastName = updatedPerson.LastName;
            p.DateOfBirth = updatedPerson.DateOfBirth;
            p.SSN = updatedPerson.SSN;
          }
        });
        this.eventDataSvc.setPeopleResults(this.eventDataSvc.getPeopleResultsValue());
      }
    }
    this.onEditPersonClose("");
  }

  private _setEventPersonTypes(eventPersonType: ServerModels.ENESS_XREF_EventsPeopleTypesDTO, eventPersonIndex: number): void {
    if (this.people[eventPersonIndex].EventPersonStagingID == eventPersonType.EventPersonStagingID) {
      let idx = this.people[eventPersonIndex].ENESS_XREF_EventsPeopleTypes.findIndex(x => x.PersonTypeID == eventPersonType.PersonTypeID);
      this.people[eventPersonIndex].ENESS_XREF_EventsPeopleTypes[idx] = eventPersonType;
    }
  }

  private _isMissingPersonType(): boolean {
    return this.people.some(p => p.ENESS_XREF_EventsPeopleTypes && p.ENESS_XREF_EventsPeopleTypes.length === 0);
  }

  private _getPersonResults(personId: string): void {
    if (AppPermissions.canViewResults) {
      this.peopleService.getPersonResults(personId).pipe(takeUntil(this.destroyed$)).subscribe((result: { 'InvestigativeTrackingNumber': string, 'CreatedOn': Date }[]) => this.peopleService.personResultsSubj.next(result));
    }
  }

  private _getPersonInfoReports(personId: string): void {
    if (AppPermissions.canViewIRs) {
      this.peopleService.getPersonInfoReports(personId).pipe(takeUntil(this.destroyed$)).subscribe((result: { 'IntelligenceTrackingNumber': string, 'CreatedOn': Date }[]) => this.peopleService.personInfoReportsSubj.next(result));
    }
  }

  private _getPersonLECases(personId: string): void {

    this.peopleService.getPersonLECases(personId).pipe(takeUntil(this.destroyed$)).subscribe((result: ServerModels.PersonLECases[]) => this.peopleService.personLECasesSubj.next(result));
  }

  private _onPersonFilterValueChange(filterId: string): void {
    if (filterId === 'Name' || filterId === 'SSN' || filterId === 'DateOfBirth' || filterId === 'PhoneNumber') {
      this.filterCtrlType = filterId;
    } else {
      this.filterCtrlType = 'FilterInput';
    }
    this.findPersonFormGroup.updateValueAndValidity();
  }

  private _onRemovePerson(idx: number): void {
    this.people.splice(idx, 1);
    this.eventPeopleFormControl.setValue([...this.people]);

    if (this.people.length % 20 == 0) {
      this.peopleTable.reset();
    }
  }

  private _isDuplicate(personStagingId: string): boolean {
    return this.people.some(x => personStagingId === x.PersonID || personStagingId === x.PersonStagingID);
  }

  private _resetFindPersonForm(): void {
    this.resetPersonFormFields();
    this.eventDataSvc.setPeopleResults([]);
    this.selectedPeople = [];
  }

  private resetPersonFormFields() {
    this.findPersonFormGroup.reset({
      FilterID: '',
      FilterInput: '',
      FilterDate: '',
      FilterSSN: '',
      FilterFirstNameInput: '',
      FilterLastNameInput: '',
      FilterPhoneNumber: ''
    });
  }

}
